import { useCallback, useEffect, useRef, useState } from 'react';
import { track } from './track';
import {
  readChecklist, writeChecklist, hasFirstRunFired, setFirstRunFired
} from './storage';

export const BASE_STEPS = ['place', 'wire', 'run', 'meter'];
const POLL_MS = 1000;
// The board counts as loaded once its component count holds for two polls after this long...
const SETTLE_MIN_MS = 2000;
// ...or after this long whatever the engine says (isOpening can stay true on a blank lab)
const SETTLE_MAX_MS = 15000;

// Cheap reads of the engine (Board.hx, @:expose("Board")). Null when the globals are missing or throw.
const readBoard = () => {
  try {
    const board = window.Board && window.Board.selectedBoard;
    if (!board || typeof board.IsSimulationOn !== 'function' || !Array.isArray(board.Components)) return null;
    return {
      board,
      simOn: board.IsSimulationOn() === true,
      count: board.Components.length,
      meters: Array.isArray(board.TwoStickMeters) ? board.TwoStickMeters.length : 0,
      opening: board.isOpening === true
    };
  } catch (e) {
    return null;
  }
};

// Parts vs wires (SparkLabAPI.hx getBoardState: wires come back as type "wire"). Only called when
// the component count changes, because it walks every component.
const readParts = () => {
  try {
    const api = window.SparkLabAPI;
    if (!api || typeof api.getBoardState !== 'function') return null;
    const state = api.getBoardState();
    const components = state && Array.isArray(state.components) ? state.components : null;
    if (!components) return null;
    let parts = 0;
    let wires = 0;
    components.forEach((component) => {
      if (component && component.type === 'wire') wires += 1;
      else parts += 1;
    });
    return { parts, wires };
  } catch (e) {
    return null;
  }
};

/**
 * First-session checklist state, derived from the engine by a 1 s poll.
 *
 * Steps: place a part, wire it, run the simulation, read an instrument (+ save for subscribers).
 * place / wire tick when the user adds a part / wire after the board has loaded, so a starter
 * circuit does not tick them on its own. "run" (and first_run) needs the simulation on, at least
 * one part and one wire, and a user action since load: a board change or turning the power on.
 * "meter" needs a meter on the board while the simulation runs, after the same user action.
 *
 * Polls only while something is pending: a checklist step, first_run, or the starter panel
 * waiting for the canvas to fill. Missing engine globals are a silent no-op (available stays false).
 */
const useFirstSession = ({ enabled, isLabLoaded, includeSave, source, isGuest, watchBoardEmpty, expectsCircuit }) => {
  const stepIds = includeSave ? [...BASE_STEPS, 'save'] : BASE_STEPS;

  const [checklist, setChecklist] = useState(() => readChecklist());
  const [available, setAvailable] = useState(false);
  const [boardFilled, setBoardFilled] = useState(false);
  const [firstRunDone, setFirstRunDone] = useState(() => hasFirstRunFired());

  const availableRef = useRef(false);
  const checklistRef = useRef(checklist);
  const firstRunRef = useRef(firstRunDone);
  const engine = useRef({
    board: null, settled: false, settleStart: 0, stableTicks: 0, lastCount: -1,
    baselineCount: 0, lastParts: null, parts: { parts: 0, wires: 0 },
    sawSimOff: false, powerActed: false, boardChanged: false, wasOpening: false
  });

  const checklistActive = !checklist.dismissed && !checklist.completed;

  // Tick one step: persist, fire checklist_step, and checklist_complete when every step is done
  const tickStep = useCallback((step) => {
    const current = checklistRef.current;
    if (current.dismissed || current.completed || current.steps[step]) return;
    const steps = { ...current.steps, [step]: true };
    const completed = stepIds.every((id) => steps[id]);
    const next = { ...current, steps, completed };
    checklistRef.current = next;
    writeChecklist(next);
    setChecklist(next);
    track('checklist_step', { step });
    if (completed) track('checklist_complete', { steps: stepIds.length });
  }, [includeSave]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissChecklist = useCallback(() => {
    const next = { ...checklistRef.current, dismissed: true };
    checklistRef.current = next;
    writeChecklist(next);
    setChecklist(next);
  }, []);

  const markSaved = useCallback(() => {
    if (!enabled || !includeSave) return;
    tickStep('save');
  }, [enabled, includeSave, tickStep]);

  // A stored checklist that is already complete for this user's steps (e.g. no save step) is done
  useEffect(() => {
    const current = checklistRef.current;
    if (!enabled || current.completed || current.dismissed) return;
    if (stepIds.every((id) => current.steps[id])) {
      const next = { ...current, completed: true };
      checklistRef.current = next;
      writeChecklist(next);
      setChecklist(next);
    }
  }, [enabled, includeSave]); // eslint-disable-line react-hooks/exhaustive-deps

  const pending = enabled && isLabLoaded && (checklistActive || !firstRunDone || (watchBoardEmpty && !boardFilled));

  useEffect(() => {
    if (!pending) return undefined;

    const poll = () => {
      const reading = readBoard();
      if (!reading) return;
      // Both globals must answer before anything renders
      if (!availableRef.current) {
        if (!readParts()) return;
        availableRef.current = true;
        setAvailable(true);
      }

      const s = engine.current;
      const now = Date.now();

      // A new board, or the engine starting to reopen a circuit (slide change, load): new baseline
      if (reading.board !== s.board || (s.settled && reading.opening && !s.wasOpening)) {
        s.board = reading.board;
        s.settled = false;
        s.settleStart = now;
        s.stableTicks = 0;
        s.lastCount = -1;
        // "Since load": what the user did to the previous circuit does not count for this one
        s.sawSimOff = false;
        s.powerActed = false;
        s.boardChanged = false;
      }
      s.wasOpening = reading.opening;

      if (reading.count !== s.lastCount) {
        const parts = readParts();
        if (parts) {
          if (s.settled && s.lastParts) {
            if (parts.parts > s.lastParts.parts) tickStep('place');
            if (parts.wires > s.lastParts.wires) tickStep('wire');
          }
          s.lastParts = parts;
          s.parts = parts;
        }
      }

      if (!s.settled) {
        s.stableTicks = reading.count === s.lastCount ? s.stableTicks + 1 : 0;
        s.lastCount = reading.count;
        const elapsed = now - s.settleStart;
        // A blank lab has nothing to open, and the engine may never clear isOpening there
        const opening = expectsCircuit && reading.opening;
        if ((elapsed >= SETTLE_MIN_MS && s.stableTicks >= 2 && !opening) || elapsed >= SETTLE_MAX_MS) {
          s.settled = true;
          s.baselineCount = reading.count;
          s.sawSimOff = !reading.simOn;
        }
        return;
      }
      s.lastCount = reading.count;

      if (reading.count > 0) setBoardFilled(true);
      if (reading.count !== s.baselineCount) s.boardChanged = true;
      if (!reading.simOn) s.sawSimOff = true;
      else if (s.sawSimOff) s.powerActed = true;

      const running = reading.simOn && s.parts.parts >= 1 && s.parts.wires >= 1 &&
        (s.boardChanged || s.powerActed);
      if (running) {
        tickStep('run');
        if (!firstRunRef.current) {
          firstRunRef.current = true;
          setFirstRunFired();
          setFirstRunDone(true);
          track('first_run', { source, is_guest: isGuest });
        }
      }
      if (reading.simOn && reading.meters > 0 && (s.boardChanged || s.powerActed)) tickStep('meter');
    };

    poll();
    const handle = setInterval(poll, POLL_MS);
    return () => clearInterval(handle);
  }, [pending, tickStep, source, isGuest, expectsCircuit]);

  return {
    available: enabled && available,
    stepIds,
    steps: checklist.steps,
    showChecklist: enabled && available && checklistActive,
    boardFilled,
    dismissChecklist,
    markSaved
  };
};

export default useFirstSession;
